import * as Yup from 'yup';
import { SearchQueryOperator } from 'Generated/schema';
import { DataLakeType } from 'Source/constants';
import { FilterKeyType } from '../types';
import { Filter, Operator } from './types';
import { TimeFrameEnum } from './CrossLogSearch/types';

const filtersRow = Yup.array<Filter>().of(
  Yup.object().shape({
    key: Yup.object()
      .nullable()
      .when('keyType', keyType => {
        if (keyType === FilterKeyType.String) {
          return Yup.string().required('This field is required');
        }

        return Yup.object().required('This field is required');
      }),
    operator: Yup.object().nullable().required('This field is required'),
    value: Yup.string().when('operator', operator => {
      if (operator?.type === null) {
        return Yup.string().nullable();
      }
      if (operator?.type === 'text') {
        return Yup.string().required('This field is required');
      }
      if (operator?.type === 'text-with-tags') {
        return Yup.array()
          .of(Yup.string())
          .min(1, 'This field is required')
          .required('This field is required');
      }
      if (operator?.type === 'number') {
        return Yup.number().required('This field is required');
      }
      if (operator?.type === 'date') {
        return Yup.date().required('This field is required');
      }
      if (operator?.type === 'date-range') {
        return Yup.array()
          .of(Yup.string().required('This field is required'))
          .required('This field is required');
      }
      if (operator?.type === 'number-range') {
        return Yup.array()
          .test('is-valid-range', 'Both numbers are required', value => {
            return value?.map(Number).every((v: number) => !Number.isNaN(v));
          })
          .test('is-valid-range', 'Numbers must be different', value => {
            return value?.[0] !== value?.[1];
          });
      }
      return Yup.string();
    }),
  }),
);

const filtersValidationSchema = Yup.object().shape({
  ors: Yup.array().of(
    Yup.object().shape({
      ands: filtersRow,
    }),
  ),
});

export const requiredFiltersValidationSchema = Yup.object().shape({
  ors: Yup.array()
    .of(
      Yup.object().shape({
        ands: filtersRow
          .required('This field is required')
          .min(1, 'At least one filter is required'),
      }),
    )
    .required('This field is required')
    .min(1, 'At least one filter is required'),
});

// We don't include the tables and databases fields in the validation schema because they are not
// required. The backend can handle the case where they are not provided.
export const crossLogSearchValidationSchema = Yup.object().shape({
  timeFrame: Yup.mixed().when('usePantherFlow', {
    is: false,
    then: schema => schema.required('This field is required'),
  }),
  startTime: Yup.mixed().when('usePantherFlow', {
    is: false,
    then: schema =>
      schema.when('timeFrame', {
        is: timeFrame => timeFrame === TimeFrameEnum.Custom,
        then: _schema => _schema.required('This field is required'),
      }),
  }),
  endTime: Yup.mixed().when('usePantherFlow', {
    is: false,
    then: schema =>
      schema.when('timeFrame', {
        is: timeFrame => timeFrame === TimeFrameEnum.Custom,
        then: _schema => _schema.required('This field is required'),
      }),
  }),
  limit: Yup.number().positive().integer().min(1).required('This field is required'),
  usePantherFlow: Yup.boolean(),
  // We only want to validate the filters if usePantherFlow is false. That will allow the form
  // to submit even if there are invalid filter chips, but we're submitting PantherFlow
  filters: Yup.object().when('usePantherFlow', {
    is: false,
    then: filtersValidationSchema,
  }),
});

// Base operators that are always available for all data lake types
const BASE_OPERATORS: Operator[] = [
  /** String */
  {
    name: 'is',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.Equals,
    initialFieldValue: '',
  },
  {
    name: 'is not',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.NotEqual,
    initialFieldValue: '',
  },
  {
    name: 'is empty',
    group: 'string',
    // `null` means don't display an input at all
    type: null,
    value: SearchQueryOperator.Empty,
    // We should not submit a value for "is empty"
    initialFieldValue: null,
  },
  {
    name: 'is not empty',
    group: 'string',
    // `null` means don't display an input at all
    type: null,
    value: SearchQueryOperator.NotEmpty,
    // We should not submit a value for "is not empty"
    initialFieldValue: null,
  },
  {
    name: 'is in list',
    group: 'string',
    // allow the user to add text as tags
    type: 'text-with-tags',
    value: SearchQueryOperator.In,
    initialFieldValue: [],
  },
  {
    name: 'is not in list',
    group: 'string',
    // allow the user to add text as tags
    type: 'text-with-tags',
    value: SearchQueryOperator.NotIn,
    initialFieldValue: [],
  },
  {
    name: 'has substring',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.Contains,
    initialFieldValue: '',
  },
  {
    name: 'does not have substring',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.DoesNotContain,
    initialFieldValue: '',
  },
  {
    name: 'like',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.Like,
    initialFieldValue: '',
  },
  {
    name: 'is not like',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.NotLike,
    initialFieldValue: '',
  },
  /** Number */
  {
    name: 'equals',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.Equals,
    initialFieldValue: '',
  },
  {
    name: 'does not equal',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.NotEqual,
    initialFieldValue: '',
  },
  {
    name: 'is greater than',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.GreaterThan,
    initialFieldValue: '',
  },
  {
    name: 'is less than',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.LessThan,
    initialFieldValue: '',
  },
  {
    name: 'is greater than or equal to',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.GreaterThanOrEqualTo,
    initialFieldValue: '',
  },
  {
    name: 'is less than or equal to',
    group: 'number',
    type: 'number',
    value: SearchQueryOperator.LessThanOrEqualTo,
    initialFieldValue: '',
  },
  {
    name: 'is between',
    group: 'number',
    type: 'number-range',
    value: SearchQueryOperator.Between,
    initialFieldValue: ['', ''],
  },
  /** Array */
  {
    name: 'has',
    group: 'array',
    type: 'text',
    value: SearchQueryOperator.Has,
    initialFieldValue: '',
  },
  {
    name: 'does not have',
    group: 'array',
    type: 'text',
    value: SearchQueryOperator.DoesNotHave,
    initialFieldValue: '',
  },
  {
    name: 'has like',
    group: 'array',
    type: 'text',
    value: SearchQueryOperator.HasLike,
    initialFieldValue: '',
  },
  {
    name: 'does not have like',
    group: 'array',
    type: 'text',
    value: SearchQueryOperator.DoesNotHaveLike,
    initialFieldValue: '',
  },
  /** Boolean */
  {
    name: 'is true',
    group: 'boolean',
    type: null,
    value: SearchQueryOperator.Equals,
    initialFieldValue: true,
  },
  {
    name: 'is false',
    group: 'boolean',
    type: null,
    value: SearchQueryOperator.Equals,
    initialFieldValue: false,
  },
  /** Time */
  {
    name: 'is after',
    group: 'time',
    type: 'date',
    value: SearchQueryOperator.GreaterThan,
    initialFieldValue: '',
  },
  {
    name: 'is before',
    group: 'time',
    type: 'date',
    value: SearchQueryOperator.LessThan,
    initialFieldValue: '',
  },
  {
    name: 'is between',
    group: 'time',
    type: 'date-range',
    value: SearchQueryOperator.Between,
    initialFieldValue: ['', ''],
  },
  /** Object */
  {
    name: 'contains',
    group: 'object',
    type: 'text',
    value: SearchQueryOperator.ObjectContains,
    initialFieldValue: '',
  },
  {
    name: 'does not contain',
    group: 'object',
    type: 'text',
    value: SearchQueryOperator.ObjectDoesNotContain,
    initialFieldValue: '',
  },
  /** Universal - these operators are always available */
  {
    name: 'is null',
    group: 'universal',
    type: null,
    value: SearchQueryOperator.IsNull,
    // We should not submit a value for "is null"
    initialFieldValue: null,
  },
  {
    name: 'is not null',
    group: 'universal',
    type: null,
    value: SearchQueryOperator.IsNotNull,
    // We should not submit a value for "is not null"
    initialFieldValue: null,
  },
];

// Databricks Operators not included in BASE_OPERATORS
const DATABRICKS_ONLY_OPERATORS: Operator[] = [
  {
    name: 'has any',
    group: 'array',
    type: 'text-with-tags',
    value: SearchQueryOperator.HasAny,
    initialFieldValue: '',
  },
  {
    name: 'does not have any',
    group: 'array',
    type: 'text-with-tags',
    value: SearchQueryOperator.DoesNotHaveAny,
    initialFieldValue: '',
  },
];

// Snowflake Operators not included in BASE_OPERATORS
const SNOWFLAKE_ONLY_OPERATORS: Operator[] = [
  {
    name: 'is within CIDR',
    group: 'string',
    type: 'text',
    value: SearchQueryOperator.WithinCidr,
    initialFieldValue: '',
  },
  {
    name: 'is within CIDR',
    group: 'array',
    type: 'text',
    value: SearchQueryOperator.WithinCidr,
    initialFieldValue: '',
  },
  {
    name: 'has any',
    group: 'array',
    type: 'text-with-tags',
    value: SearchQueryOperator.HasAny,
    initialFieldValue: '',
  },
  {
    name: 'does not have any',
    group: 'array',
    type: 'text-with-tags',
    value: SearchQueryOperator.DoesNotHaveAny,
    initialFieldValue: '',
  },
  /** Row */
  {
    name: 'row contains',
    group: 'row',
    type: null,
    value: SearchQueryOperator.RowContains,
    initialFieldValue: null,
  },
  {
    name: 'row like',
    group: 'row',
    type: null,
    value: SearchQueryOperator.RowIlike,
    initialFieldValue: null,
  },
  {
    name: 'row regexp contains',
    group: 'row',
    type: null,
    value: SearchQueryOperator.RowRegexpContains,
    initialFieldValue: null,
  },
];

/**
 * Returns the list of operators available for the given data lake type.
 */
export function getOperators(dataLakeType?: DataLakeType | null): Operator[] {
  if (dataLakeType === DataLakeType.Databricks) {
    return [...BASE_OPERATORS, ...DATABRICKS_ONLY_OPERATORS];
  }
  if (dataLakeType === DataLakeType.Redshift) {
    return BASE_OPERATORS;
  }
  return [...BASE_OPERATORS, ...SNOWFLAKE_ONLY_OPERATORS];
}

// Default export for backward compatibility - returns all operators
export const OPERATORS: Operator[] = getOperators();

export const QUERY_BUILDER_SESSION_KEY = 'simple-search-query';

export const DATE_FORMAT_NO_TIMEZONE = `MM/DD/YYYY`;
export const DATE_FORMAT_WITH_TIME = `MM/DD/YYYY HH:mm`;
export const DATE_FORMAT_UTC = `MM/DD/YYYY HH:mm UTC`;
// Using square brackets will escape the "a" in "Local" and display it as a literal
// (instead of am/pm)
export const DATE_FORMAT_LOCAL = `MM/DD/YYYY HH:mm Loc[a]l`;
